import { useCallback, useEffect, useState } from 'react';
import type { BuildState, Setup, SightSelection, BowSetup } from '@/types';
import { INITIAL_BUILD } from './useBuildState';
import { DEFAULT_TAPE_OPTIONS } from '@/lib/sight-tape-presets';
import { primaryBowSetup } from '@/profiles/setupBridge';

const SETUPS_KEY = 'sparrow_setups_v2';
const LEGACY_BUILDS_KEY = 'sparrow_arrow_builds';

/**
 * The multi-setup state model — first-class Setups, each with N arrows.
 *
 * A Setup =
 *   - 1 bow (draw weight, length, IBO, type)
 *   - 1 sight + peep height + sight radius
 *   - N arrows (each a full BuildState)
 *   - Active arrow index
 *   - Sight-in calibration speed
 *
 * The active Setup's active arrow is what renders in the live preview.
 *
 * Persistence:
 *   - Writes to localStorage SETUPS_KEY on every change
 *   - On first load, if no SETUPS_KEY exists but a legacy single-build save
 *     exists, migrate it into a default Setup with that arrow.
 */
export interface UseSetups {
  setups: Setup[];
  activeSetupId: string | null;
  activeSetup: Setup | null;
  activeArrow: BuildState | null;

  // Setup CRUD
  createSetup: (name: string, bow?: BowSetup, sight?: SightSelection) => Setup;
  updateSetup: (id: string, patch: Partial<Setup>) => void;
  deleteSetup: (id: string) => void;
  setActiveSetup: (id: string) => void;
  duplicateSetup: (id: string) => Setup | null;

  // Arrow CRUD (within active setup)
  addArrow: (build?: Partial<BuildState>) => void;
  updateActiveArrow: (patch: Partial<BuildState>) => void;
  setActiveArrow: (idx: number) => void;
  deleteArrow: (idx: number) => void;
  duplicateArrow: (idx: number) => void;
}

const DEFAULT_SIGHT: SightSelection = { sightIdx: 0, pinDiameter: 0.019 };

function makeSetup(
  name: string,
  bow?: BowSetup,
  sight?: SightSelection,
  arrow?: BuildState,
): Setup {
  const now = new Date().toISOString();
  return {
    id: `setup-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    name,
    bow: bow ?? INITIAL_BUILD.bow,
    sight: sight ?? DEFAULT_SIGHT,
    peepHeight: 3.5,
    sightRadius: 26.0,
    arrows: [arrow ?? { ...INITIAL_BUILD }],
    activeArrowIdx: 0,
    sightedInYards: 20,
    // Default chrono1Yards to 0 (launch) so users only need to enter
    // chrono1Fps + chrono2Yards + chrono2Fps to enable dual-velocity mode.
    chrono1Yards: 0,
    createdAt: now,
    updatedAt: now,
  };
}

function loadFromStorage(): { setups: Setup[]; activeSetupId: string | null } {
  if (typeof window === 'undefined') return { setups: [], activeSetupId: null };

  // Try new key first
  try {
    const raw = window.localStorage.getItem(SETUPS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { setups: Setup[]; activeSetupId: string | null };
      if (Array.isArray(parsed.setups) && parsed.setups.length > 0) {
        // Migration:
        //   1. Backfill chrono1Yards default (older setups had it undefined).
        //   2. Merge new PCA-style tape range fields (startYards/endYards/
        //      incrementYards/hybrid*) into any persisted tapeOptions.
        //      Pre-v20 setups had `tickStyle` but no range — without backfill,
        //      buildYardageList reads undefined and bottoms out the tape.
        parsed.setups = parsed.setups.map((s) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { ...tapeOptsRaw } = (s.tapeOptions ?? {}) as Record<string, unknown>;
          // strip legacy field that no longer exists in the type
          delete tapeOptsRaw.tickStyle;
          const tapeOptions = {
            ...DEFAULT_TAPE_OPTIONS,
            ...tapeOptsRaw,
          } as Setup['tapeOptions'];
          return {
            ...s,
            chrono1Yards: s.chrono1Yards ?? 0,
            tapeOptions,
            // Backfill on every arrow:
            //   - vaneBackPosition (pre-v33 builds)
            //   - bow.letOff (pre-v38 builds — default 80%)
            arrows: (s.arrows ?? []).map((a) => ({
              ...a,
              vaneBackPosition: a.vaneBackPosition ?? 1.0,
              bow: { ...a.bow, letOff: a.bow?.letOff ?? 80 },
            })),
            bow: s.bow ? { ...s.bow, letOff: s.bow.letOff ?? 80 } : s.bow,
          };
        });
        return parsed;
      }
    }
  } catch { /* fall through */ }

  // Legacy migration — pull existing single-build saves into a default setup
  try {
    const raw = window.localStorage.getItem(LEGACY_BUILDS_KEY);
    if (raw) {
      const legacy = JSON.parse(raw) as Array<{ name: string; state: BuildState }>;
      if (Array.isArray(legacy) && legacy.length > 0) {
        const setup = makeSetup(legacy[0].name || 'My First Setup', undefined, undefined, legacy[0].state);
        return { setups: [setup], activeSetupId: setup.id };
      }
    }
  } catch { /* */ }

  // Fresh install — create a default setup. If the user already saved a
  // primary BowProfile (on /bows.html), seed the default Setup's bow + sight
  // from it so they don't have to re-enter the same info in Tune.
  const primary = primaryBowSetup();
  if (primary) {
    const setupName = primary.profile.name || 'Default Setup';
    const fresh = makeSetup(setupName, primary.bow, primary.sight);
    return { setups: [fresh], activeSetupId: fresh.id };
  }
  const fresh = makeSetup('Default Setup');
  return { setups: [fresh], activeSetupId: fresh.id };
}

function persistToStorage(setups: Setup[], activeSetupId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SETUPS_KEY, JSON.stringify({ setups, activeSetupId }));
  } catch { /* quota exceeded — silently swallow */ }
}

export function useSetups(): UseSetups {
  const initial = loadFromStorage();
  const [setups, setSetups] = useState<Setup[]>(initial.setups);
  const [activeSetupId, setActiveSetupId] = useState<string | null>(
    initial.activeSetupId ?? initial.setups[0]?.id ?? null,
  );

  // Auto-persist
  useEffect(() => {
    persistToStorage(setups, activeSetupId);
  }, [setups, activeSetupId]);

  const activeSetup = setups.find((s) => s.id === activeSetupId) ?? null;
  const activeArrow = activeSetup
    ? activeSetup.arrows[activeSetup.activeArrowIdx] ?? null
    : null;

  // ────────────────────────────────────────────────────────
  // Setup CRUD
  // ────────────────────────────────────────────────────────

  const createSetup = useCallback(
    (name: string, bow?: BowSetup, sight?: SightSelection): Setup => {
      const setup = makeSetup(name, bow, sight);
      setSetups((prev) => [...prev, setup]);
      setActiveSetupId(setup.id);
      return setup;
    },
    [],
  );

  const updateSetup = useCallback((id: string, patch: Partial<Setup>) => {
    setSetups((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, ...patch, updatedAt: new Date().toISOString() } : s,
      ),
    );
  }, []);

  const deleteSetup = useCallback((id: string) => {
    setSetups((prev) => {
      const next = prev.filter((s) => s.id !== id);
      // Reassign active if needed
      if (id === activeSetupId) {
        setActiveSetupId(next[0]?.id ?? null);
      }
      return next;
    });
  }, [activeSetupId]);

  const setActiveSetup = useCallback((id: string) => {
    setActiveSetupId(id);
  }, []);

  const duplicateSetup = useCallback((id: string): Setup | null => {
    const src = setups.find((s) => s.id === id);
    if (!src) return null;
    const copy: Setup = {
      ...src,
      id: `setup-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      name: `${src.name} (copy)`,
      arrows: src.arrows.map((a) => ({ ...a })),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setSetups((prev) => [...prev, copy]);
    setActiveSetupId(copy.id);
    return copy;
  }, [setups]);

  // ────────────────────────────────────────────────────────
  // Arrow CRUD (within active setup)
  // ────────────────────────────────────────────────────────

  const addArrow = useCallback((build?: Partial<BuildState>) => {
    if (!activeSetup) return;
    const newArrow: BuildState = { ...INITIAL_BUILD, ...build };
    updateSetup(activeSetup.id, {
      arrows: [...activeSetup.arrows, newArrow],
      activeArrowIdx: activeSetup.arrows.length, // jump to the new one
    });
  }, [activeSetup, updateSetup]);

  const updateActiveArrow = useCallback((patch: Partial<BuildState>) => {
    if (!activeSetup || !activeArrow) return;
    const arrows = activeSetup.arrows.map((a, i) =>
      i === activeSetup.activeArrowIdx ? { ...a, ...patch } : a,
    );
    updateSetup(activeSetup.id, { arrows });
  }, [activeSetup, activeArrow, updateSetup]);

  const setActiveArrow = useCallback((idx: number) => {
    if (!activeSetup) return;
    if (idx < 0 || idx >= activeSetup.arrows.length) return;
    updateSetup(activeSetup.id, { activeArrowIdx: idx });
  }, [activeSetup, updateSetup]);

  const deleteArrow = useCallback((idx: number) => {
    if (!activeSetup) return;
    if (activeSetup.arrows.length <= 1) return; // keep at least one arrow
    const arrows = activeSetup.arrows.filter((_, i) => i !== idx);
    const activeArrowIdx = Math.max(0, Math.min(activeSetup.activeArrowIdx, arrows.length - 1));
    updateSetup(activeSetup.id, { arrows, activeArrowIdx });
  }, [activeSetup, updateSetup]);

  const duplicateArrow = useCallback((idx: number) => {
    if (!activeSetup) return;
    const src = activeSetup.arrows[idx];
    if (!src) return;
    const copy: BuildState = { ...src, name: `${src.name} (copy)` };
    const arrows = [...activeSetup.arrows];
    arrows.splice(idx + 1, 0, copy);
    updateSetup(activeSetup.id, { arrows, activeArrowIdx: idx + 1 });
  }, [activeSetup, updateSetup]);

  return {
    setups,
    activeSetupId,
    activeSetup,
    activeArrow,
    createSetup,
    updateSetup,
    deleteSetup,
    setActiveSetup,
    duplicateSetup,
    addArrow,
    updateActiveArrow,
    setActiveArrow,
    deleteArrow,
    duplicateArrow,
  };
}
